import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { KeenIcon } from '@/components/keenicons';
import { Container } from '@/components/common/container';
import { adminDashboardService } from '@/services/admin';
import { feedbackService } from '@/services/feedback.service';

const DASHBOARD_REFRESH_MS = 30_000;
const STATS_REFRESH_MS = 60_000;

const formatNumber = (value?: number) =>
  typeof value === 'number' ? value.toLocaleString() : '—';

const formatTimestamp = (value?: string) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
};

const resolveHealthKey = (status?: string) => {
  const normalized = status?.toLowerCase() ?? '';
  if (normalized.includes('unhealthy') || normalized.includes('down') || normalized.includes('error')) {
    return 'error';
  }
  if (normalized.includes('degraded') || normalized.includes('warning')) {
    return 'warning';
  }
  if (normalized.includes('healthy')) {
    return 'healthy';
  }
  return 'unknown';
};

const serviceIconMap: Record<string, string> = {
  Identity: 'shield-tick',
  User: 'profile-circle',
  Engagement: 'gift',
  Analytics: 'chart-line-2',
  Communication: 'notification',
  Wallet: 'dollar',
  Gateway: 'setting-2',
};

/**
 * Admin Dashboard - Main Overview Page
 *
 * Features:
 * - System health monitoring
 * - Key performance indicators (KPIs)
 * - Real-time statistics
 * - Quick actions and shortcuts
 * - Recent activity feed
 *
 * Based on Epic E3: Dashboard & Analytics from docs/src
 */
export function AdminDashboard() {
  const dashboardQuery = useQuery({
    queryKey: ['admin-dashboard', 'overview'],
    queryFn: () => adminDashboardService.getDashboard(),
    refetchInterval: DASHBOARD_REFRESH_MS,
  });

  const userStatsQuery = useQuery({
    queryKey: ['admin-dashboard', 'user-stats'],
    queryFn: () => adminDashboardService.getUserStatistics(),
    refetchInterval: STATS_REFRESH_MS,
  });

  const feedbackQuery = useQuery({
    queryKey: ['admin-dashboard', 'feedback', 'new'],
    queryFn: () =>
      feedbackService.getList({
        status: 'New',
        skipCount: 0,
        maxResultCount: 1,
      }),
    refetchInterval: STATS_REFRESH_MS,
  });

  const healthBadge = useMemo(() => {
    if (dashboardQuery.isLoading) {
      return { label: 'Checking services…', variant: 'secondary' as const };
    }

    const services = dashboardQuery.data?.services ?? [];
    if (services.length === 0) {
      return { label: 'No health data', variant: 'warning' as const };
    }

    const unhealthyCount = services.filter((service) =>
      ['error'].includes(resolveHealthKey(service.status))
    ).length;
    if (unhealthyCount > 0) {
      return {
        label: `${unhealthyCount} service${unhealthyCount === 1 ? '' : 's'} down`,
        variant: 'destructive' as const,
      };
    }

    const degradedCount = services.filter(
      (service) => resolveHealthKey(service.status) === 'warning'
    ).length;
    if (degradedCount > 0) {
      return {
        label: `${degradedCount} service${degradedCount === 1 ? '' : 's'} degraded`,
        variant: 'warning' as const,
      };
    }

    return { label: 'All Systems Operational', variant: 'success' as const };
  }, [dashboardQuery.data, dashboardQuery.isLoading]);

  return (
    <Container>
      <div className="flex flex-col gap-6 py-6">
        {/* Page Header */}
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Monitor and manage your Tagna platform
          </p>
          <p className="text-xs text-muted-foreground">
            Last updated {formatTimestamp(dashboardQuery.data?.lastUpdated)} • Auto-refreshes every 30s
            {dashboardQuery.isFetching ? ' • refreshing…' : ''}
          </p>
        </div>

        {/* System Health Status */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>System Health</CardTitle>
                <CardDescription>Real-time system status monitoring</CardDescription>
              </div>
              <Badge variant={healthBadge.variant}>
                <KeenIcon icon="check-circle" className="mr-1" />
                {healthBadge.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {dashboardQuery.isLoading && (
                <>
                  <HealthIndicator label="Loading" icon="setting-2" isLoading />
                  <HealthIndicator label="Loading" icon="setting-2" isLoading />
                  <HealthIndicator label="Loading" icon="setting-2" isLoading />
                  <HealthIndicator label="Loading" icon="setting-2" isLoading />
                </>
              )}
              {!dashboardQuery.isLoading &&
                (dashboardQuery.data?.services ?? []).map((service) => (
                  <HealthIndicator
                    key={service.name}
                    label={service.name}
                    status={service.status}
                    icon={serviceIconMap[service.name] || 'setting-2'}
                  />
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Users"
            value={formatNumber(userStatsQuery.data?.totalUsers)}
            subtext={
              userStatsQuery.data
                ? `${formatNumber(userStatsQuery.data.newUsersToday)} new today`
                : undefined
            }
            icon="profile-circle"
            iconColor="text-blue-600"
            bgColor="bg-blue-50"
            isLoading={userStatsQuery.isLoading}
          />
          <MetricCard
            title="Active Users"
            value={formatNumber(userStatsQuery.data?.activeUsers)}
            subtext={
              userStatsQuery.data
                ? `${formatNumber(userStatsQuery.data.inactiveUsers)} inactive`
                : undefined
            }
            icon="users"
            iconColor="text-green-600"
            bgColor="bg-green-50"
            isLoading={userStatsQuery.isLoading}
          />
          <MetricCard
            title="Healthy Services"
            value={formatNumber(dashboardQuery.data?.healthyServices)}
            subtext={
              dashboardQuery.data
                ? `of ${formatNumber(dashboardQuery.data.totalServices)} total`
                : undefined
            }
            icon="shield-tick"
            iconColor="text-emerald-600"
            bgColor="bg-emerald-50"
            isLoading={dashboardQuery.isLoading}
          />
          <MetricCard
            title="New Feedback"
            value={formatNumber(feedbackQuery.data?.totalCount)}
            subtext="Awaiting triage"
            icon="message-text"
            iconColor="text-orange-600"
            bgColor="bg-orange-50"
            isLoading={feedbackQuery.isLoading}
          />
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common administrative tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <QuickActionCard
                title="User Management"
                description="Manage users, roles, and permissions"
                icon="profile-circle"
                href="/admin/users"
              />
              <QuickActionCard
                title="Prize Draws"
                description="Manage lottery and prize draws"
                icon="gift"
                href="/admin/prize-draws"
              />
              <QuickActionCard
                title="Announcements"
                description="Create and manage announcements"
                icon="notification"
                href="/admin/announcements"
              />
              <QuickActionCard
                title="Settings"
                description="System configuration and settings"
                icon="setting-2"
                href="/admin/settings"
              />
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity (Placeholder) */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest system events and user actions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <KeenIcon icon="chart-line-2" className="text-4xl mb-2" />
              <p>Activity feed coming soon...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Container>
  );
}

/**
 * Health Indicator Component
 */
interface HealthIndicatorProps {
  label: string;
  status?: string;
  icon: string;
  isLoading?: boolean;
}

function HealthIndicator({ label, status, icon, isLoading }: HealthIndicatorProps) {
  const statusConfig = {
    healthy: { color: 'text-green-600', bg: 'bg-green-50', badge: 'Healthy' },
    warning: { color: 'text-yellow-600', bg: 'bg-yellow-50', badge: 'Warning' },
    error: { color: 'text-red-600', bg: 'bg-red-50', badge: 'Error' },
    unknown: { color: 'text-slate-600', bg: 'bg-slate-50', badge: 'Unknown' },
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg border">
        <div className="p-2 rounded-lg bg-muted/40">
          <Skeleton className="h-6 w-6" />
        </div>
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-16" />
        </div>
      </div>
    );
  }

  const resolved = resolveHealthKey(status);
  const config = statusConfig[resolved as keyof typeof statusConfig];
  const badgeLabel = status ?? config.badge;

  return (
    <div className="flex items-center gap-3 p-4 rounded-lg border">
      <div className={`p-2 rounded-lg ${config.bg}`}>
        <KeenIcon icon={icon} className={`text-2xl ${config.color}`} />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium">{label}</p>
        <Badge variant="outline" className={`mt-1 ${config.color}`}>
          {badgeLabel}
        </Badge>
      </div>
    </div>
  );
}

/**
 * Metric Card Component
 */
interface MetricCardProps {
  title: string;
  value: string;
  icon: string;
  iconColor: string;
  bgColor: string;
  change?: string;
  trend?: 'up' | 'down';
  subtext?: string;
  isLoading?: boolean;
}

function MetricCard({
  title,
  value,
  change,
  trend,
  icon,
  iconColor,
  bgColor,
  subtext,
  isLoading,
}: MetricCardProps) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            {isLoading ? (
              <Skeleton className="h-9 w-24 mt-2" />
            ) : (
              <p className="text-3xl font-bold mt-2">{value}</p>
            )}
            {change && trend ? (
              <div className="flex items-center gap-1 mt-2">
                <KeenIcon
                  icon={trend === 'up' ? 'arrow-up' : 'arrow-down'}
                  className={`text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}
                />
                <span
                  className={`text-sm font-medium ${
                    trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {change}
                </span>
                <span className="text-sm text-muted-foreground">vs last month</span>
              </div>
            ) : subtext ? (
              <p className="text-sm text-muted-foreground mt-2">{subtext}</p>
            ) : null}
          </div>
          <div className={`p-3 rounded-lg ${bgColor}`}>
            <KeenIcon icon={icon} className={`text-3xl ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Quick Action Card Component
 */
interface QuickActionCardProps {
  title: string;
  description: string;
  icon: string;
  href: string;
}

function QuickActionCard({ title, description, icon, href }: QuickActionCardProps) {
  return (
    <a
      href={href}
      className="flex flex-col gap-2 p-4 rounded-lg border hover:border-primary hover:bg-accent transition-colors cursor-pointer"
    >
      <div className="flex items-center gap-2">
        <KeenIcon icon={icon} className="text-2xl text-primary" />
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </a>
  );
}

export default AdminDashboard;
